import {
  DateRange,
  DueDisplayOptions,
  ID,
  IDPrefixes,
  PrecomputedCompletion,
  ScheduleEntryState,
  Task,
  TaskActionType,
  TaskType,
  Timestamp,
  UpdateTask,
} from '@griply/types';
import {
  getTaskDeadline,
  getWeekStartsOn,
  isTimeslotAction,
} from '@griply/utils';
import { isSameDay } from 'date-fns';
import { useGoals } from 'features/goals';
import { useTaskTodayControls } from 'features/tasks';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TaskCardHeader } from 'shared/components/connected/task-card-header';
import { TaskMetaInteractions } from 'shared/components/connected/task-meta-interactions';
import { useDragPreview } from 'shared/components/ui/virtualized-tree-list';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useClickOutside } from 'shared/hooks/use-click-outside';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { useDevRenderOutline } from 'shared/hooks/use-dev-render-outline';
import { useIsMultiSelectModifierPressed } from 'shared/hooks/use-is-multi-select-modifier-pressed';
import { useRelationshipIdsById } from 'shared/hooks/use-relationships';
import { useTaskDisplayDate } from 'shared/hooks/use-task-display-date';
import { useUser } from 'shared/hooks/use-user';
import { InteractionType } from 'shared/types/data-type';
import { eventIsFromInteractionType } from 'shared/utils/event-is-from-interaction-type';
import { useTheme } from 'styled-components';

import { ContextMenu } from './context-menu';
import * as Styled from './task-card.style';

export type TaskCardProps = {
  task: Task;
  displayDate?: Timestamp;
  displayDateRange?: DateRange;
  completionDate?: Timestamp;
  displayDue?: DueDisplayOptions[];
  onClick?: (id: ID) => void;
  onUnfocus?: (id: ID) => void;
  showDetailOnSelect?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  showRepeatingAsTodo?: boolean;
  showRepeatingAsDoneOnDateEntry?: boolean;
  hideGoal?: boolean;
  hideLifeArea?: boolean;
  hideTimeslot?: boolean;

  isMoving?: boolean;
  isGrabbable?: boolean;
  isMultiSelectable?: boolean;
  hideContextMenu?: boolean;
  hideEdit?: boolean;
  preventInteraction?: boolean;
  forceChecked?: boolean;
  onCustomComplete?: () => void;
  onCustomUpdate?: (task: Task) => void;
  onCustomDelete?: () => void;
  onCustomDuplicate?: () => void;
  tagIds?: ID[];
  precomputedCompletion?: PrecomputedCompletion;
  // When the represented date has a completed occurrence, show it (checked)
  // instead of resolving forward to the next uncompleted occurrence.
  includeCompletedOccurrence?: boolean;
};

export const TaskCard: React.FC<TaskCardProps> = React.memo(
  ({
    task,
    displayDate,
    displayDateRange,
    completionDate,
    displayDue,
    onClick,
    onUnfocus,
    showDetailOnSelect = true,
    isSelected,
    isDisabled,
    showRepeatingAsTodo,

    hideGoal,
    hideLifeArea,
    hideTimeslot,

    isMoving,
    isGrabbable,
    isMultiSelectable,
    hideContextMenu,
    hideEdit,
    preventInteraction,
    forceChecked,
    onCustomComplete,
    onCustomUpdate,
    onCustomDelete,
    onCustomDuplicate,
    tagIds,
    precomputedCompletion,
    includeCompletedOccurrence,
  }) => {
    const theme = useTheme();
    const containerRef = useRef<HTMLDivElement>(null);
    const isDragPreview = useDragPreview();
    useDevRenderOutline(containerRef, isDragPreview);
    const [contextMenuOpen, setContextMenuOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const { tag: tagRelationships } = useRelationshipIdsById(
      task.id,
      IDPrefixes.Task,
    );
    const isMultiSelectModifierPressed = useIsMultiSelectModifierPressed();

    const today = useToday();
    const user = useUser();

    const weekStartsOn = getWeekStartsOn(user);

    const openTaskDetail = useOpenTaskDetail();

    const goals = useGoals();

    const actionGoal = useMemo(
      () =>
        task.action?.type === TaskActionType.GoalTimeslot
          ? goals.find(({ id }) => id === task.action?.meta.id)
          : undefined,
      [goals, task.action],
    );

    const deadlineDeadline = useMemo(
      () => getTaskDeadline(task, actionGoal),
      [task, actionGoal],
    );

    const isActionTimeslot = isTimeslotAction(task.action?.type);

    const referenceDate = displayDate ?? completionDate ?? today;
    const dateToRepresent = useTaskDisplayDate(task, referenceDate, {
      today,
      weekStartsOn,
      dateRange: displayDateRange,
      uncompletedOnly: !includeCompletedOccurrence,
    });

    const hideInteractions = useMemo(
      () =>
        (!task.goalId || (hideGoal && !task.archivedAt)) &&
        (!task.lifeAreaId || (hideLifeArea && !task.archivedAt)) &&
        (!task.timeslot?.startTime || hideTimeslot) &&
        (!task.timeslot?.duration || hideTimeslot) &&
        !task.endStrategy?.deadline &&
        !task.schedules?.length &&
        !tagRelationships.length &&
        !tagIds?.length,
      [
        hideGoal,
        hideLifeArea,
        hideTimeslot,
        tagRelationships.length,
        tagIds?.length,
        task.endStrategy?.deadline,
        task.goalId,
        task.lifeAreaId,
        task.schedules?.length,
        task.timeslot?.duration,
        task.timeslot?.startTime,
        task.archivedAt,
      ],
    );

    const { onComplete, onUpdate, openManualEntryLogging } =
      useTaskTodayControls();
    const { position, open, close } = useContextMenuState({
      disabled: theme.isMobile || !!task.frozenAt,
    });

    const _onClick = (e: React.MouseEvent) => {
      // when multi-selectable, the onClick is cancelled when shift/ctrl/cmd is pressed as the user is multi-selecting at that moment
      if (isMultiSelectable && (e.shiftKey || e.ctrlKey || e.metaKey)) {
        return;
      }

      // Stop propagation to prevent the tree from selecting the item
      // and triggering a re-render of all items in the virtualized list
      e.stopPropagation();

      if (task.type !== TaskType.Task && !isSameDay(dateToRepresent, today)) {
        open(e);
        return;
      }
      showDetailOnSelect && !task.frozenAt && openTaskDetail(task);
      onClick?.(task.id);
    };

    const _onContextMenuOpen = () => setContextMenuOpen(true);

    const _onContextMenuClose = () => {
      setContextMenuOpen(false);
      close();
    };

    const _onComplete = (date: Timestamp) =>
      onComplete(task, ScheduleEntryState.Complete, {
        // completionDate is only used on the completions page, so when it does not exist, we can use the date
        referenceDate: completionDate ?? date,
        toggleDayEntry: showRepeatingAsTodo,
      });

    const _onEntryChange = (
      type: ScheduleEntryState,
      options?: { value?: number; increment?: number },
    ) =>
      onComplete(task, type, {
        ...options,
        referenceDate: completionDate,
        toggleDayEntry: showRepeatingAsTodo,
      });

    const onManual = [TaskType.Habit, TaskType.Repeating].includes(task.type)
      ? (date: Timestamp) =>
          openManualEntryLogging(task.id, completionDate ?? date)
      : undefined;

    useClickOutside(containerRef, (e) => {
      if (eventIsFromInteractionType(e, InteractionType.Task)) {
        return;
      }

      if (isSelected) {
        onUnfocus?.(task.id);
      }
    });

    useEffect(() => {
      if (!isSelected) {
        setIsHovered(false);
      }
    }, [isSelected]);

    const handleMetaUpdate = (t: Task, updates: UpdateTask) => {
      if (onCustomUpdate) {
        onCustomUpdate({ ...t, ...updates } as Task);
        return;
      }

      onUpdate(t, updates);
    };

    return (
      <ContextMenu
        task={task}
        onOpen={isDisabled ? undefined : _onContextMenuOpen}
        onClose={_onContextMenuClose}
        externalPosition={position}
        timeslotDate={dateToRepresent}
        disabled={hideContextMenu}
        onUpdate={handleMetaUpdate}
        onDelete={onCustomDelete}
        onDuplicate={onCustomDuplicate}
        hideEdit={hideEdit}
      >
        <Styled.Container
          ref={containerRef}
          onClick={isDisabled ? undefined : _onClick}
          $isSelected={isSelected || contextMenuOpen || !!position}
          $isMoving={!!isMoving}
          $isFrozen={!!task.frozenAt}
          $isGrabbable={!!isGrabbable}
        >
          <TaskCardHeader
            task={task}
            onComplete={_onComplete}
            onEntryChange={_onEntryChange}
            onEntryManual={onManual}
            showRepeatingAsTodo={showRepeatingAsTodo}
            isDisabled={isDisabled}
            weekStartsOn={getWeekStartsOn(user)}
            dateToRepresent={dateToRepresent}
            displayDue={displayDue}
            today={today}
            forceChecked={forceChecked}
            onCustomComplete={onCustomComplete}
            precomputedCompletion={precomputedCompletion}
            deadlineDeadline={deadlineDeadline}
          />
          <Styled.Body>
            {!hideInteractions && (
              <TaskMetaInteractions
                task={task}
                onUpdate={handleMetaUpdate}
                displayDate={dateToRepresent}
                hasTooltips={isHovered}
                hideGoal={
                  isActionTimeslot ||
                  (!task.archivedAt && !isSelected && !isHovered && hideGoal)
                }
                hideLifeArea={
                  isActionTimeslot ||
                  (!task.archivedAt &&
                    !isSelected &&
                    !isHovered &&
                    hideLifeArea)
                }
                displayDue={displayDue}
                preventInteraction={
                  theme.isMobile ||
                  preventInteraction ||
                  (isMultiSelectable && isMultiSelectModifierPressed)
                }
                tagIds={tagIds}
              />
            )}
          </Styled.Body>
        </Styled.Container>
      </ContextMenu>
    );
  },
);
